import { Service } from '@internal/blocks/SearchResultsBlock/ISearchResultsBlockData';
import {
    FinancingProvider,
    getLeasingProvider,
} from '@internal/utils/machine/leasing';
import { ImageLoader } from '@internal/utils/media/ImageLoader';
import { UserContext } from '@internal/utils/user/UserContext';
import DllLogo from '@renderer-ui-library/assets/companyLogos/dllLogo.png';
import EFarmFinancingLogo from '@renderer-ui-library/assets/companyLogos/efarmFinancing.svg';
import { Icon } from '@renderer-ui-library/atoms';
import { Typography } from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import classnames from 'classnames';
import Image from 'next/image';
import React, { useCallback, useContext } from 'react';
import { getMediaSrc } from 'utils/media/getMediaSrc';
import { tracker } from 'utils/tracking/tracker';
import { ActionableButton } from '../ActionableButton/ActionableButton';
import styles from './productServiceCard.module.scss';

type Props = {
    service: Service;
};

export const ProductServiceCard: React.FC<Props> = React.memo(({ service }) => {
    const { isMobile } = useMediaQuery();
    const { user } = useContext(UserContext);
    const leasingProvider = getLeasingProvider(user.countryCode);

    const handleButtonClick = useCallback(() => {
        tracker.trackEvent({
            name: 'click',
            target_name: 'service_card.serp',
            is_interaction: true,
        });
    }, []);

    return (
        <div className={styles.wrapper}>
            {service.image?.data && (
                <div className={styles.image}>
                    <img
                        {...getMediaSrc(service.image.data, {
                            ratio: 1 / 1,
                        })}
                        loading="lazy"
                        alt={service.image.data.attributes.alternativeText}
                    />
                </div>
            )}

            <div className={styles.content}>
                {service.icon && (
                    <Icon icon={service.icon.icon} className={styles.icon} />
                )}
                <div
                    className={classnames(styles.title, {
                        [styles.titleSmMargin]:
                            service.serviceType === 'leasing',
                    })}
                >
                    <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        color="secondary"
                    >
                        {service.title}
                    </Typography>
                </div>
                {service.logo?.data && (
                    <div className={styles.logo}>
                        <img
                            {...getMediaSrc(service.logo.data, {
                                ratio: 1 / 1,
                            })}
                            loading="lazy"
                            alt={service.logo.data.attributes.alternativeText}
                        />
                    </div>
                )}
                {leasingProvider && (
                    <div className={styles.leasingLogo}>
                        <Image
                            src={`${
                                leasingProvider === FinancingProvider.Dll
                                    ? DllLogo.src
                                    : EFarmFinancingLogo.src
                            }?tr=w-132`}
                            alt=""
                            loader={ImageLoader}
                            width={132}
                            height={36}
                        />
                    </div>
                )}
                {service.button && (
                    <div className={styles.button}>
                        <ActionableButton
                            button={service.button}
                            enabled
                            noMinWidth
                            variant={service.buttonVariant}
                            color="inherit"
                            onClick={handleButtonClick}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});

ProductServiceCard.displayName = 'ProductServiceCard';
